import { setDiagnosticSink } from '@antification/core';
import { Game } from './engine/Game';

/**
 * Give the simulation somewhere to report the impossible.
 *
 * `packages/core` cannot name `console` — its `tsconfig` has no `DOM` library —
 * so the handful of "this should not have happened" reports inside it go to an
 * injected sink, and the browser is the thing that knows the sink is a console.
 * Wired here rather than in `Game` because it is a property of the *host*, and it
 * should be in place before anything constructs a district.
 */
setDiagnosticSink((message, detail) =>
  detail === undefined ? console.error(message) : console.error(message, detail)
);

// Hide loading screen when game is ready
const loading = document.getElementById('loading');

// Initialize game
const game = new Game();

game.init().then(() => {
  loading?.classList.add('hidden');
  game.start();
}).catch((error) => {
  console.error('Failed to initialize game:', error);
  if (loading) {
    loading.innerHTML = `
      <h1>ANTIFICATION</h1>
      <p style="color: #c00;">Development opportunity unavailable. Please refresh.</p>
    `;
  }
});

// Handle window resize
window.addEventListener('resize', () => {
  game.resize();
});

// Handle view toggle (Tab key)
window.addEventListener('keydown', (e) => {
  if (e.key === 'Tab' && !game.isHelpVisible()) {
    e.preventDefault();
    game.toggleView();
  }
});

// Fullscreen toggle
const fullscreenBtn = document.getElementById('fullscreen-btn');
const gameContainer = document.getElementById('game-container');

function toggleFullscreen(): void {
  const doc = document as Document & {
    webkitFullscreenElement?: Element;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
  };
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
    mozRequestFullScreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
  };
  const exitFn = document as Document & {
    webkitExitFullscreen?: () => Promise<void>;
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
  };

  const isFullscreen = doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement;

  if (!isFullscreen) {
    // Enter fullscreen
    if (docEl.requestFullscreen) {
      docEl.requestFullscreen();
    } else if (docEl.webkitRequestFullscreen) {
      docEl.webkitRequestFullscreen();
    } else if (docEl.mozRequestFullScreen) {
      docEl.mozRequestFullScreen();
    } else if (docEl.msRequestFullscreen) {
      docEl.msRequestFullscreen();
    }
  } else {
    // Exit fullscreen
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (exitFn.webkitExitFullscreen) {
      exitFn.webkitExitFullscreen();
    } else if (exitFn.mozCancelFullScreen) {
      exitFn.mozCancelFullScreen();
    } else if (exitFn.msExitFullscreen) {
      exitFn.msExitFullscreen();
    }
  }
}

fullscreenBtn?.addEventListener('click', toggleFullscreen);

// Help toggle
const helpBtn = document.getElementById('help-btn');
helpBtn?.addEventListener('click', () => {
  game.toggleHelp();
});

// Handle fullscreen change to resize game
document.addEventListener('fullscreenchange', () => {
  // Small delay to let browser settle
  setTimeout(() => game.resize(), 100);
});
document.addEventListener('webkitfullscreenchange', () => {
  setTimeout(() => game.resize(), 100);
});

// View toggle button for mobile
const viewToggleBtn = document.getElementById('view-toggle-btn');
viewToggleBtn?.addEventListener('click', () => {
  game.toggleView();
});

// Double-tap on screen divider to toggle view (mobile gesture)
let lastTapTime = 0;
const topScreen = document.getElementById('top-screen');
const bottomScreen = document.getElementById('bottom-screen');

function handleDoubleTap(e: TouchEvent): void {
  const now = Date.now();
  const timeSinceLastTap = now - lastTapTime;

  if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
    e.preventDefault();
    game.toggleView();
  }

  lastTapTime = now;
}

// Add double-tap listeners to screen edges for view toggle
topScreen?.addEventListener('touchend', (e) => {
  const rect = topScreen.getBoundingClientRect();
  const touch = e.changedTouches[0];
  // Check if touch was near the bottom edge (last 50px)
  if (touch.clientY > rect.bottom - 50) {
    handleDoubleTap(e);
  }
});

bottomScreen?.addEventListener('touchend', (e) => {
  const rect = bottomScreen.getBoundingClientRect();
  const touch = e.changedTouches[0];
  // Check if touch was near the top edge (first 50px)
  if (touch.clientY < rect.top + 50) {
    handleDoubleTap(e);
  }
});

// Expose game instance for iframe communication
declare global {
  interface Window {
    antificationGame: Game;
  }
}
window.antificationGame = game;
